/**
 * EditModals.tsx - Reusable Edit Modal configurations for XUM + Riders
 *
 * Provides pre-configured modal states for the M07/M13 result screens.
 * Each function returns:
 *   - fields: EditField[] for the modal
 *   - isOpen / open / close helpers
 *
 * Modal IDs:
 *   - 'xum-main' → XUM main plan (9 fields) — matches 1_xanh_uoc_mo_sp_chinh_edit.png
 *   - 'xum-topup' → Top-up tab — matches 1_xanh_uoc_mo_dau_tu_them.png
 *   - 'r1' → Sống Khỏe Mỗi Ngày — matches 2_song_khoe_moi_ngay_edit.png
 *   - 'r2' → Lá Chắn Xanh (3 sub-options) — matches 3_la_chan_xanh_edit.png
 *   - 'r3' → Dự Phòng Xanh — matches 4_du_phong_xanh_edit.png
 *   - 'r4' → Hộ Vệ Xanh — matches 5_ho_ve_xanh_edit.png
 *   - 'r5' → Bảo Thân Xanh — matches 6_bao_than_xanh_edit.png
 */

import { useCallback, useState } from 'react';
import { EditField } from '../components/EditModal';
import { PRODUCTS } from '../data/products';

const XUM_CONFIG = PRODUCTS.find((p) => p.id === 'XUM')?.config;

export type EditModalId =
  | 'xum-main'
  | 'xum-topup'
  | 'r1'
  | 'r2'
  | 'r3'
  | 'r4'
  | 'r5';

/**
 * Helper hook: tracks which modal is open + values for each field.
 * Returns open/close/field-update handlers and a map of current values.
 */
export interface EditModalsState {
  openId: EditModalId | null;
  open: (id: EditModalId) => void;
  close: () => void;
  values: Record<string, string>;
  setValue: (key: string, v: string) => void;
  reset: (defaults: Record<string, string>) => void;
}

export function useEditModals(initialDefaults: Record<string, string> = {}): EditModalsState {
  const [openId, setOpenId] = useState<EditModalId | null>(null);
  const [values, setValues] = useState<Record<string, string>>(initialDefaults);

  const open = useCallback((id: EditModalId) => setOpenId(id), []);
  const close = useCallback(() => setOpenId(null), []);
  const setValue = useCallback((key: string, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  }, []);
  const reset = useCallback(
    (defaults: Record<string, string>) => setValues((prev) => ({ ...prev, ...defaults })),
    [],
  );

  return { openId, open, close, values, setValue, reset };
}

/**
 * Build EditField[] for XUM main plan modal
 * Matches Curex 1_xanh_uoc_mo_sp_chinh_edit.png (9 fields)
 */
export function buildXumMainFields(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
): EditField[] {
  const num = (s: string) => parseInt((s || '0').replace(/\D/g, ''), 10) || 0;
  const sa = num(values.sumAssured || '0');
  const saWarning =
    XUM_CONFIG &&
    (sa < XUM_CONFIG.minSumAssured || sa > XUM_CONFIG.maxSumAssured)
      ? `Ngoài phạm vi: ${XUM_CONFIG.minSumAssured.toLocaleString('vi-VN')} - ${XUM_CONFIG.maxSumAssured.toLocaleString('vi-VN')} đ`
      : null;

  return [
    {
      index: 1,
      label: 'Sản phẩm',
      type: 'dropdown',
      value: 'Xanh Ước Mơ',
      onChange: () => {},
    },
    {
      index: 2,
      label: 'Kế hoạch bảo hiểm',
      type: 'dropdown',
      value: values.plan || 'Cơ bản',
      onChange: (v) => setValue('plan', v),
      options: [
        { label: 'Cơ bản', value: 'Cơ bản' },
        { label: 'Nâng cao', value: 'Nâng cao' },
      ],
    },
    {
      index: 3,
      label: 'Phí mỗi năm',
      type: 'number',
      value: values.basePremium || '0',
      onChange: (v) => setValue('basePremium', v),
      hint: XUM_CONFIG ? `Tối thiểu: ${XUM_CONFIG.minBasePremium.toLocaleString('vi-VN')} đ/năm` : undefined,
      prefix: 'đ',
    },
    {
      index: 4,
      label: 'Quyền lợi bảo hiểm năm đầu',
      type: 'number',
      value: values.sumAssured || '0',
      onChange: (v) => setValue('sumAssured', v),
      hint: XUM_CONFIG
        ? `Tối thiểu: ${XUM_CONFIG.minSumAssured.toLocaleString('vi-VN')} - Tối đa: ${XUM_CONFIG.maxSumAssured.toLocaleString('vi-VN')}`
        : undefined,
      prefix: 'đ',
      warning: saWarning,
    },
    {
      index: 5,
      label: 'Số năm đóng phí',
      type: 'stepper',
      value: values.term || '20',
      onChange: (v) => setValue('term', v),
      min: XUM_CONFIG?.minTerm ?? 10,
      max: XUM_CONFIG?.maxTerm ?? 20,
      step: 1,
      hint: XUM_CONFIG ? `Tối đa: ${XUM_CONFIG.maxTerm} năm` : undefined,
    },
    {
      index: 6,
      label: 'Quỹ đầu tư',
      type: 'stepper',
      value: '100',
      onChange: () => {},
      min: 0,
      max: 100,
      step: 1,
      hint: 'Quỹ Liên Kết Chung (mặc định)',
    },
    {
      index: 7,
      label: 'Đầu tư thêm',
      type: 'number',
      value: values.topUpAmount || '0',
      onChange: (v) => setValue('topUpAmount', v),
      hint: 'Số tiền đầu tư thêm mỗi năm (>= 0)',
      prefix: 'đ',
    },
    {
      index: 8,
      label: 'Năm bắt đầu đóng thêm',
      type: 'stepper',
      value: values.topUpStartYear || '1',
      onChange: (v) => setValue('topUpStartYear', v),
      min: 1,
      max: 20,
      step: 1,
      hint: 'Tối đa: 20 năm',
    },
    {
      index: 9,
      label: 'Số năm đầu tư thêm',
      type: 'stepper',
      value: values.topUpDuration || '0',
      onChange: (v) => setValue('topUpDuration', v),
      min: 0,
      max: 20,
      step: 1,
    },
  ];
}

/**
 * Build EditField[] for XUM Top-up modal (read-only summary tab)
 */
export function buildXumTopupFields(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
): EditField[] {
  return [
    {
      index: 1,
      label: 'Đầu tư thêm mỗi năm',
      type: 'number',
      value: values.topUpAmount || '0',
      onChange: (v) => setValue('topUpAmount', v),
      prefix: 'đ',
    },
    {
      index: 2,
      label: 'Số năm đầu tư thêm',
      type: 'stepper',
      value: values.topUpDuration || '0',
      onChange: (v) => setValue('topUpDuration', v),
      min: 0,
      max: 20,
      step: 1,
    },
  ];
}

/**
 * R1 Sống Khỏe Mỗi Ngày — matches 2_song_khoe_moi_ngay_edit.png
 * Fields:
 *   01 Loại gói hạn mức (Bạc/Vàng/Bạch Kim)
 *   02 Sản phẩm bổ trợ (checkbox + dropdown gói)
 */
export function buildR1Fields(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
): EditField[] {
  return [
    {
      index: 1,
      label: 'Loại gói hạn mức',
      type: 'dropdown',
      value: values.r1Package || 'Bạc',
      onChange: (v) => setValue('r1Package', v),
      options: [
        { label: 'Bạc', value: 'Bạc' },
        { label: 'Vàng', value: 'Vàng' },
        { label: 'Bạch Kim', value: 'Bạch Kim' },
      ],
    },
    {
      index: 2,
      label: 'Sản phẩm bổ trợ - Quyền lợi Điều Trị Nội Trú',
      type: 'checkbox',
      value: values.r1Include || 'true',
      onChange: (v) => setValue('r1Include', v),
    },
  ];
}

/**
 * R2 Lá Chắn Xanh — matches 3_la_chan_xanh_edit.png
 * Fields:
 *   01 Ngân quỹ bảo vệ bệnh nghiêm trọng (input)
 *   02 Sản phẩm bổ trợ:
 *     - Quyền lợi Bệnh Lý Nghiêm Trọng Giai Đoạn Cuối (checkbox)
 *     - Quyền lợi Bệnh Lý Nghiêm Trọng Giai Đoạn Sớm (input ≤ 25M)
 *     - Quyền lợi Bệnh Lý Nghiêm Trọng Đặc Biệt (input ≤ 25M)
 */
export function buildR2Fields(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
): EditField[] {
  return [
    {
      index: 1,
      label: 'Ngân quỹ bảo vệ bệnh nghiêm trọng',
      type: 'number',
      value: values.r2MainAmount || '100000000',
      onChange: (v) => setValue('r2MainAmount', v),
      prefix: 'đ',
    },
    {
      index: 2,
      label: 'Sản phẩm bổ trợ',
      type: 'checkbox',
      value: values.r2IncludeCuoi || 'true',
      onChange: (v) => setValue('r2IncludeCuoi', v),
    },
    {
      index: 3,
      label: 'Quyền lợi Bệnh Lý Nghiêm Trọng Giai Đoạn Sớm',
      type: 'number',
      value: values.r2SomAmount || '0',
      onChange: (v) => setValue('r2SomAmount', v),
      hint: 'Tối đa: 25.000.000 đ',
      prefix: 'đ',
    },
    {
      index: 4,
      label: 'Quyền lợi Bệnh Lý Nghiêm Trọng Đặc Biệt',
      type: 'number',
      value: values.r2DacBietAmount || '0',
      onChange: (v) => setValue('r2DacBietAmount', v),
      hint: 'Tối đa: 25.000.000 đ',
      prefix: 'đ',
    },
  ];
}

/**
 * R3 Dự Phòng Xanh — matches 4_du_phong_xanh_edit.png
 * Fields:
 *   01 Ngân quỹ bù đắp thu nhập (input)
 */
export function buildR3Fields(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
): EditField[] {
  return [
    {
      index: 1,
      label: 'Ngân quỹ bù đắp thu nhập',
      type: 'number',
      value: values.r3Amount || '1000000',
      onChange: (v) => setValue('r3Amount', v),
      prefix: 'đ',
    },
  ];
}

/**
 * R4 Hộ Vệ Xanh — matches 5_ho_ve_xanh_edit.png
 * Fields:
 *   01 Ngân quỹ bảo vệ tổn thương thể chất (input)
 */
export function buildR4Fields(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
): EditField[] {
  return [
    {
      index: 1,
      label: 'Ngân quỹ bảo vệ tổn thương thể chất',
      type: 'number',
      value: values.r4Amount || '100000000',
      onChange: (v) => setValue('r4Amount', v),
      prefix: 'đ',
    },
  ];
}

/**
 * R5 Bảo Thân Xanh — matches 6_bao_than_xanh_edit.png
 * Fields:
 *   01 Loại gói hạn mức (20 Năm / Trọn đời)
 *   02 Số tiền bảo hiểm (input)
 */
export function buildR5Fields(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
): EditField[] {
  return [
    {
      index: 1,
      label: 'Loại gói hạn mức',
      type: 'dropdown',
      value: values.r5Package || '20 Năm',
      onChange: (v) => setValue('r5Package', v),
      options: [
        { label: '20 Năm', value: '20 Năm' },
        { label: 'Trọn đời', value: 'Trọn đời' },
      ],
    },
    {
      index: 2,
      label: 'Số tiền bảo hiểm',
      type: 'number',
      value: values.r5SumAssured || '200000000',
      onChange: (v) => setValue('r5SumAssured', v),
      prefix: 'đ',
    },
  ];
}

/**
 * Returns EditField[] for the given modal id.
 */
export function buildFieldsFor(
  id: EditModalId,
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
): EditField[] {
  switch (id) {
    case 'xum-main':
      return buildXumMainFields(values, setValue);
    case 'xum-topup':
      return buildXumTopupFields(values, setValue);
    case 'r1':
      return buildR1Fields(values, setValue);
    case 'r2':
      return buildR2Fields(values, setValue);
    case 'r3':
      return buildR3Fields(values, setValue);
    case 'r4':
      return buildR4Fields(values, setValue);
    case 'r5':
      return buildR5Fields(values, setValue);
    default:
      return [];
  }
}

/**
 * Returns modal title/subtitle for the given id.
 */
export function getModalTitle(id: EditModalId): { title: string; subtitle?: string } {
  switch (id) {
    case 'xum-main':
      return { title: 'Chỉnh sửa sản phẩm', subtitle: 'Xanh Ước Mơ' };
    case 'xum-topup':
      return { title: 'Đầu tư thêm' };
    case 'r1':
      return { title: 'Chỉnh sửa sản phẩm', subtitle: 'Sống Khỏe Mỗi Ngày' };
    case 'r2':
      return { title: 'Thiết lập sản phẩm', subtitle: 'Lá Chắn Xanh' };
    case 'r3':
      return { title: 'Chỉnh sửa sản phẩm', subtitle: 'Dự Phòng Xanh' };
    case 'r4':
      return { title: 'Thiết lập sản phẩm', subtitle: 'Hộ Vệ Xanh' };
    case 'r5':
      return { title: 'Chỉnh sửa sản phẩm', subtitle: 'Bảo Thân Xanh' };
  }
}